/** Public blog article — markdown + data widgets, SEO meta + JSON-LD. */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, CircleNotch, Translate } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { BlogWidget } from "../components/blog/BlogWidgets";
import BlogQa from "../components/blog/BlogQa";
import BlogPoll from "../components/blog/BlogPoll";
import { useLang } from "../contexts/LanguageContext";
import { blogPublicGet, blogShareText, blogTranslate } from "../lib/blog-api";

function setMeta(name, content, property = false) {
    const attr = property ? "property" : "name";
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

const MD_COMPONENTS = {
    h2: (p) => <h2 className="font-display font-black text-xl uppercase tracking-tight mt-8 mb-3 text-fa-cream" {...p} />,
    h3: (p) => <h3 className="font-display font-bold text-lg mt-6 mb-2" {...p} />,
    p: (p) => <p className="text-white/75 leading-relaxed mb-3" {...p} />,
    li: (p) => <li className="text-white/75 leading-relaxed ml-5 list-disc mb-1" {...p} />,
    strong: (p) => <strong className="text-white font-bold" {...p} />,
    img: (p) => <img className="w-full border border-white/10 my-5" alt={p.alt || ""} {...p} data-testid="blog-inline-image" />,
    table: (p) => (
        <div className="overflow-x-auto my-5 border border-white/10" data-testid="blog-md-table">
            <table className="w-full min-w-[520px] border-collapse text-sm" {...p} />
        </div>
    ),
    thead: (p) => <thead className="bg-white/5" {...p} />,
    th: (p) => <th className="text-left font-mono text-[10px] uppercase tracking-widest text-fa-cream px-3 py-2 border-b border-white/15 whitespace-nowrap" {...p} />,
    td: (p) => <td className="text-white/75 px-3 py-2 border-b border-white/5 align-top" {...p} />,
    tr: (p) => <tr className="even:bg-white/[0.02]" {...p} />,
};

export default function BlogPost() {
    const { slug } = useParams();
    const { lang } = useLang();
    const [post, setPost] = useState(null);
    const [err, setErr] = useState(null);
    const [sharing, setSharing] = useState(false);
    const [en, setEn] = useState(null);
    const [showEn, setShowEn] = useState(false);
    const [translating, setTranslating] = useState(false);

    const toggleLang = async () => {
        if (showEn) { setShowEn(false); return; }
        if (en) { setShowEn(true); return; }
        setTranslating(true);
        try {
            const tr = await blogTranslate(slug);
            setEn(tr);
            setShowEn(true);
        } catch (e) { toast.error(e.response?.data?.detail || "Překlad se nepodařil"); }
        finally { setTranslating(false); }
    };

    const shareOnX = async () => {
        if (sharing || !post) return;
        setSharing(true);
        const win = window.open("about:blank", "_blank");
        let text = `${post.title} — ${post.perex || ""}`.slice(0, 220);
        try {
            const r = await blogShareText(slug);
            if (r?.text) text = r.text;
        } catch { /* fallback na jednoduchý text */ }
        const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
        if (win) win.location.href = intent;
        else window.open(intent, "_blank");
        setSharing(false);
    };

    useEffect(() => {
        setEn(null);
        setShowEn(false);
        blogPublicGet(slug).then(setPost).catch(() => setErr("Článek nenalezen."));
    }, [slug]);

    useEffect(() => {
        if (lang === "en" && post && !showEn && !translating) toggleLang();
        if (lang === "cs" && showEn) setShowEn(false);
    }, [lang, post]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!post) return;
        document.title = `${post.title} | Fantasy Academy Blog`;
        setMeta("description", post.seo_description || post.perex || "");
        const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/blog/${post.hero_status === "ready" ? "hero" : "og"}/${post.slug}.png`;
        setMeta("og:title", post.title, true);
        setMeta("og:description", post.seo_description || post.perex || "", true);
        setMeta("og:image", ogUrl, true);
        setMeta("og:type", "article", true);
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:image", ogUrl);
        const ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "blog-jsonld";
        ld.text = JSON.stringify({
            "@context": "https://schema.org", "@type": "Article",
            headline: post.title, description: post.seo_description || post.perex,
            datePublished: post.published_at, dateModified: post.updated_at,
            inLanguage: "cs", about: post.player_name,
            author: { "@type": "Organization", name: "Fantasy Academy" },
        });
        document.getElementById("blog-jsonld")?.remove();
        document.head.appendChild(ld);
        return () => document.getElementById("blog-jsonld")?.remove();
    }, [post]);

    const activeContent = showEn && en ? en.content_md : post?.content_md;
    const parts = (activeContent || "").split(/\[\[widget:([a-z0-9_]+)\]\]/g);

    return (
        <AppShell>
            <div className="max-w-3xl mx-auto space-y-4" data-testid="blog-post-page">
                <Link to="/blog" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white inline-flex items-center gap-1">
                    <ArrowLeft size={11} /> Blog
                </Link>
                {err && <p className="font-mono text-[11px] text-red-300">{err}</p>}
                {post && (
                    <article>
                        <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-white/40">
                            <span>{post.published_at ? new Date(post.published_at).toLocaleDateString("cs") : ""}</span>
                            {post.player_name && <span className="text-fa-mint">{post.player_name}</span>}
                            {(post.tags || []).map((t) => <span key={t} className="border border-white/15 px-1.5 py-0.5">{t}</span>)}
                        </div>
                        <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-2 mb-2" data-testid="blog-post-title">{showEn && en ? en.title : post.title}</h1>
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <button onClick={shareOnX} disabled={sharing} data-testid="blog-share-x"
                                    className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-white/25 text-white/70 hover:border-white hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
                                {sharing ? <CircleNotch size={11} className="animate-spin" /> : "𝕏"}
                                {sharing ? "Připravuji tweet…" : "Sdílet na X"}
                            </button>
                            <button onClick={toggleLang} disabled={translating} data-testid="blog-translate-toggle"
                                    className={`font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5 disabled:opacity-60 ${showEn ? "border-fa-sky bg-fa-sky/10 text-fa-sky" : "border-white/25 text-white/70 hover:border-white hover:text-white"}`}>
                                {translating ? <CircleNotch size={11} className="animate-spin" /> : <Translate size={11} />}
                                {translating ? "Překládám…" : showEn ? "Zpět na češtinu" : "Read in English"}
                            </button>
                        </div>
                        <p className="text-white/60 text-base italic border-l-2 border-fa-mint pl-3 mb-4">{showEn && en ? en.perex : post.perex}</p>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/blog/${post.hero_status === "ready" ? "hero" : "og"}/${post.slug}.png`} alt={post.title}
                             className="w-full border border-white/10 mb-6" data-testid="blog-post-hero" />
                        {parts.map((part, i) => i % 2 === 1
                            ? <BlogWidget key={i} name={part} widgets={post.widgets} />
                            : <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{part}</ReactMarkdown>
                        )}
                        <BlogPoll slug={slug} />
                        <BlogQa slug={slug} />
                    </article>
                )}
            </div>
        </AppShell>
    );
}
